type ErrorPayload = {
    url: string;
    name?: string;
    message?: string;
    stack?: string;
    filename?: string;
    line?: number;
    col?: number;
    source: 'window.error' | 'unhandledrejection' | 'react' | 'create order fetch' | 'stripe.load';
    fallback?: boolean;
    timestamp: number;
};

type ErrorSender = (error: ErrorPayload) => void | Promise<void>;

/* ------------------------------------------------------------------ */
/* Internal state                                                      */
/* ------------------------------------------------------------------ */

const errorBuffer: ErrorPayload[] = [];
let errorSender: ErrorSender | null = null;

const recentStacks = new Set<string>();
let fallbackTimerStarted = false;

/* ------------------------------------------------------------------ */
/* Utilities                                                           */
/* ------------------------------------------------------------------ */

function shouldLog(error: ErrorPayload): boolean {
    if (!error.stack) return true;

    if (recentStacks.has(error.stack)) {
        return false;
    }

    recentStacks.add(error.stack);
    setTimeout(() => recentStacks.delete(error.stack!), 5000);

    return true;
}

export function enqueue(error: ErrorPayload) {
    if (!shouldLog(error)) return;

    if (errorSender) {
        safeSend(errorSender, error);
    } else {
        errorBuffer.push(error);
        ensureFallbackTimer();
    }
}

function safeSend(sender: ErrorSender, error: ErrorPayload) {
    try {
        const result = sender(error);
        if (result instanceof Promise) {
            result.catch(() => {
                sendFallback(error);
            });
        }
    } catch {
        sendFallback(error);
    }
}

/* ------------------------------------------------------------------ */
/* Timeout fallback (critical fix)                                     */
/* ------------------------------------------------------------------ */

function ensureFallbackTimer() {
    if (fallbackTimerStarted) return;

    fallbackTimerStarted = true;

    setTimeout(() => {
        if (errorSender) return;

        // No sender ever registered → flush via beacon
        while (errorBuffer.length) {
            sendFallback(errorBuffer.shift()!);
        }
    }, 2000);
}

/* ------------------------------------------------------------------ */
/* Fallback transport (fatal-safe)                                     */
/* ------------------------------------------------------------------ */

function sendFallback(error: ErrorPayload) {
    try {
        navigator.sendBeacon(
            '/logs/logerror',
            JSON.stringify({
                ...error,
                fallback: true,
            })
        );
    } catch {
        // nothing left to do
    }
}

/* ------------------------------------------------------------------ */
/* Public API                                                          */
/* ------------------------------------------------------------------ */

/**
 * Call this ONCE when Amplify / auth context is ready
 */
export function registerErrorSender(sender: ErrorSender) {
    errorSender = sender;

    // Flush buffered errors immediately
    while (errorBuffer.length) {
        safeSend(sender, errorBuffer.shift()!);
    }
}

/**
 * Optional: explicit React ErrorBoundary hook
 */
export function logReactError(error: Error) {
    enqueue({
        url: document.location.href,
        name: error.name,
        message: error.message,
        stack: error.stack,
        source: 'react',
        timestamp: Date.now(),
    });
}

/* ------------------------------------------------------------------ */
/* Global handlers (attached immediately)                              */
/* ------------------------------------------------------------------ */

window.addEventListener('error', (event: ErrorEvent) => {
    enqueue({
        url: document.location.href,
        name: event.error?.name,
        message: event.error?.message || event.message,
        stack: event.error?.stack,
        filename: event.filename,
        line: event.lineno,
        col: event.colno,
        source: 'window.error',
        timestamp: Date.now(),
    });
});

window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const error = event.reason;

    enqueue({
        url: document.location.href,
        name: error?.name || 'UnhandledPromiseRejection',
        message: error?.message || String(error),
        stack: error?.stack,
        source: 'unhandledrejection',
        timestamp: Date.now(),
    });

    event.preventDefault();
});
